import { choiceChips } from '../../app/choice-filters.js';
import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { formatPhone } from '@hue/contracts';
import {
  Button,
  DateRangePicker,
  IconButton,
  Modal,
  StatusText,
  formatCivilRange,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, UserRound, UsersRound, Eye, Archive } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  clientsApi,
  getApiError,
  type Client,
  type ClientAccessFilter,
  type ClientActivityFilter,
  type ClientSort,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { ClientProfile } from './client-profile.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  MultiFilter,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { ClientsView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { ClientCpfSearch } from '../../app/client-cpf-search.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

/// Um ícone de outro módulo na linha do cliente — o do Prontuário, por exemplo. Clientes não sabe
/// quem o desenha: a composição entrega a função, com os clientes da página para que todos os ícones
/// leiam numa consulta só.
export type ClientRowAddon = (client: Client, pageClientIds: readonly string[]) => ReactNode;

export interface ClientCapabilities {
  canCreate: boolean;
  canUpdate: boolean;
  canArchive: boolean;
  canManageAccess: boolean;
}

const activityOptions: Array<{ value: Exclude<ClientActivityFilter, 'ALL'>; label: string }> = [
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INACTIVE', label: 'Arquivados' },
];
const accessOptions: Array<{ value: Exclude<ClientAccessFilter, 'ALL'>; label: string }> = [
  { value: 'LINKED', label: 'Com acesso' },
  { value: 'UNLINKED', label: 'Sem acesso' },
];
const columns: Array<{ sort: ClientSort; label: string }> = [
  { sort: 'displayName', label: 'Cliente' },
  { sort: 'createdAt', label: 'Cadastrado em' },
];

export function CompanyClients({
  capabilities,
  timeZone,
  today,
  view,
  onView,
  onOpenClient,
  rowAddon,
}: {
  capabilities: ClientCapabilities;
  timeZone: string;
  today: CivilDate;
  view: ClientsView;
  onView: ViewSetter<ClientsView>;
  /// Abrir o cliente é ir à página dele. Quem sabe o endereço é a composição.
  onOpenClient: (clientId: string) => void;
  rowAddon?: ClientRowAddon | undefined;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [activationTarget, setActivationTarget] = useState<Client>();
  const [activationError, setActivationError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    activity: view.activity,
    kind: view.kind,
    access: view.access,
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'clients', filters],
    queryFn: () => clientsApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const pageClientIds = (page.data?.clients ?? []).map((client) => client.id);
  const addon = (client: Client) => rowAddon?.(client, pageClientIds);
  function toggleSort(column: ClientSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  async function setActive(client: Client) {
    setActivationError(undefined);
    try {
      await clientsApi.setActive(client.id, !client.active);
      await queryClient.invalidateQueries({ queryKey: ['company', 'clients'] });
      setActivationTarget(undefined);
      snackbar.success(client.active ? 'Cliente arquivado.' : 'Cliente reativado.');
    } catch (error) {
      setActivationError(getApiError(error));
    }
  }

  const total = page.data?.total ?? 0;
  const chips = [
    ...choiceChips('activity', view.activity, activityOptions, (activity) => onView({ activity })),
    ...choiceChips('access', view.access, accessOptions, (access) => onView({ access })),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  return (
    <section aria-labelledby="company-clients-title">
      <PageHeader
        trail="Cadastros / Clientes"
        id="company-clients-title"
        title="Clientes"
        description="Cadastros próprios da empresa, com acesso ao portal opcional."
        actions={
          <>
            {capabilities.canCreate ? (
              <CreateButton onClick={() => setCreateOpen(true)}>Cadastrar cliente</CreateButton>
            ) : null}
          </>
        }
      />

      <div className="mb-5 max-w-md">
        <ClientCpfSearch includeArchived actionLabel="Abrir" onSelect={(id) => onOpenClient(id)} />
      </div>

      <FilterBar
        searchId="client-search"
        searchLabel="Buscar cliente"
        searchPlaceholder="Nome, documento, e-mail ou telefone"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: [], kind: 'ALL', access: [], from: null, to: null })}
        count={`${total} ${total === 1 ? 'cliente' : 'clientes'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'cliente' : 'clientes'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todas as situações"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <MultiFilter
          label="Portal"
          itemNoun="condições de acesso"
          allLabel="Todos os clientes"
          options={accessOptions}
          value={view.access}
          onChange={(access) => onView({ access })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Cadastro</span>
          <DateRangePicker
            label="Dias de cadastro"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={today}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando clientes" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={UsersRound}
          title="Nenhum cliente encontrado"
          description="Ajuste os filtros ou cadastre o primeiro cliente da empresa."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label="Ordenação dos clientes"
                columns={columns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.clients.map((client) => (
                  <li key={client.id}>
                    <ClientCard
                      client={client}
                      capabilities={capabilities}
                      onOpen={() => onOpenClient(client.id)}
                      onActivation={() => setActivationTarget(client)}
                      addon={addon(client)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Clientes cadastrados na empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="px-5 py-3 text-xs font-semibold text-content/70">
                    Contato e portal
                  </th>
                  <th className={rowAddon ? 'w-32' : 'w-24'}>
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.clients.map((client) => (
                  <tr
                    key={client.id}
                    className="cursor-pointer transition hover:bg-content/5"
                    onClick={() => onOpenClient(client.id)}
                  >
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        className="block max-w-full cursor-pointer truncate text-left text-sm font-semibold hover:text-brand-400 hover:underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenClient(client.id);
                        }}
                      >
                        {client.displayName}
                      </button>
                      {client.active ? null : (
                        <StatusText tone="neutral" className="mt-1 text-xs">
                          Arquivado
                        </StatusText>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDate(client.createdAt, timeZone)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      <span className="block truncate">
                        {client.email ?? formatPhone(client.phone) ?? 'Sem contato'}
                      </span>
                      <AccessStatus client={client} className="mt-1" />
                    </td>
                    <td className="px-3 py-4">
                      <Actions
                        client={client}
                        capabilities={capabilities}
                        onEdit={() => onOpenClient(client.id)}
                        onActivation={() => setActivationTarget(client)}
                        addon={addon(client)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'cliente' : 'clientes')}
          />
        </div>
      )}

      <ClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        client={null}
        capabilities={capabilities}
        today={today}
        timeZone={timeZone}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'clients'] });
          setCreateOpen(false);
          snackbar.success('Cliente cadastrado.');
        }}
      />
      {activationTarget ? (
        <Modal
          open
          onOpenChange={() => setActivationTarget(undefined)}
          title={activationTarget.active ? 'Arquivar cliente' : 'Reativar cliente'}
          description={
            activationTarget.active
              ? 'O cadastro e o histórico são preservados; o acesso ao portal deixa de funcionar.'
              : 'O cadastro volta a ficar disponível para a operação.'
          }
        >
          <p className="text-sm">
            {activationTarget.active ? 'Arquivar' : 'Reativar'}{' '}
            <strong>{activationTarget.displayName}</strong>?
          </p>
          {activationError ? <RequestAlert message={activationError} /> : null}
          <div className="mt-7 flex justify-end gap-3 border-t border-surface-border pt-5">
            <Button variant="outline" onClick={() => setActivationTarget(undefined)}>
              Cancelar
            </Button>
            <Button onClick={() => void setActive(activationTarget)}>
              {activationTarget.active ? 'Arquivar cliente' : 'Reativar cliente'}
            </Button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

function ClientCard({
  client,
  capabilities,
  onOpen,
  onActivation,
  addon,
}: {
  client: Client;
  capabilities: ClientCapabilities;
  onOpen: () => void;
  onActivation: () => void;
  addon: ReactNode;
}) {
  return (
    <div className="cursor-pointer p-4 transition hover:bg-content/5" onClick={onOpen}>
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
          <UserRound className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="block max-w-full cursor-pointer truncate text-left text-sm font-semibold"
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            {client.displayName}
          </button>
          <span className="text-xs text-content-muted">
            {client.email ?? formatPhone(client.phone) ?? 'Sem contato informado'}
          </span>
          <div className="mt-1 text-xs">
            <AccessStatus client={client} />
          </div>
        </div>
        <Actions
          client={client}
          capabilities={capabilities}
          onEdit={onOpen}
          onActivation={onActivation}
          addon={addon}
        />
      </div>
    </div>
  );
}

function AccessStatus({ client, className }: { client: Client; className?: string }) {
  const active = client.accesses.filter((access) => access.status === 'ACTIVE').length;
  const invited = client.accesses.filter((access) => access.status === 'INVITED').length;
  if (active)
    return (
      <StatusText tone="positive" className={className}>
        {active} {active === 1 ? 'acesso ativo' : 'acessos ativos'}
      </StatusText>
    );
  if (invited)
    return (
      <StatusText tone="progress" className={className}>
        Convite pendente
      </StatusText>
    );
  return (
    <StatusText tone="neutral" className={className}>
      Sem acesso
    </StatusText>
  );
}

function Actions({
  client,
  capabilities,
  onEdit,
  onActivation,
  addon,
}: {
  client: Client;
  capabilities: ClientCapabilities;
  onEdit: () => void;
  onActivation: () => void;
  addon: ReactNode;
}) {
  /// A linha e o cartão inteiros abrem o cliente; os ícones fazem só o que dizem.
  return (
    <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
      {addon}
      {capabilities.canUpdate || capabilities.canManageAccess ? (
        <IconButton label="Abrir cliente" icon={<Eye className="size-4" />} onClick={onEdit} />
      ) : null}
      {capabilities.canArchive ? (
        <IconButton
          label={client.active ? 'Arquivar' : 'Reativar'}
          {...(client.active ? { tone: 'danger' as const } : {})}
          icon={client.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />}
          onClick={onActivation}
        />
      ) : null}
    </div>
  );
}

function ClientDialog({
  open,
  onOpenChange,
  client,
  capabilities,
  today,
  timeZone,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
  capabilities: ClientCapabilities;
  today: CivilDate;
  timeZone: string;
  onSaved: () => Promise<void>;
}) {
  const title = client ? 'Ficha do cliente' : 'Cadastrar cliente';
  const description = 'O cadastro pertence somente a esta empresa e não exige uma conta de acesso.';
  /// Fechado, a ficha não existe: reabrir começa de um formulário limpo.
  if (!open) return null;
  return (
    <ClientProfile
      client={client}
      capabilities={capabilities}
      today={today}
      timeZone={timeZone}
      onSaved={onSaved}
      onCancel={() => onOpenChange(false)}
      frame={(body, actions) => (
        <Modal
          open
          onOpenChange={onOpenChange}
          title={title}
          description={description}
          className="max-w-3xl"
          footer={actions}
        >
          {body}
        </Modal>
      )}
    />
  );
}
